import React, { useCallback } from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';

import ChevronDown from '@atlaskit/icon/core/chevron-down';
import DropdownMenu from '@atlaskit/dropdown-menu/dropdown-menu';
import DropdownItem from '@atlaskit/dropdown-menu/dropdown-menu-item';
import DropdownItemGroup from '@atlaskit/dropdown-menu/dropdown-menu-item-group';
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-atlaskit-theme
import { layers } from '@atlaskit/theme/constants';
import Button from '@atlaskit/button/button';
import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';

import { COVERPICTUREWIDTH } from './contentTopperConstants.ts';

const i18n = defineMessages({
	fixedWidthSelectOption: {
		id: 'content-topper.page-title.popup.fixed-width.option',
		defaultMessage: 'Fixed width',
		description: 'Text for select dropdown option to control header image width to a smaller size',
	},
	fullWidthSelectOption: {
		id: 'content-topper.page-title.popup.full-width.option',
		defaultMessage: 'Full width',
		description: 'Text for select dropdown option to control header image width to a larger size',
	},
	selectWidthLabel: {
		id: 'content-topper.page-title.popup.select-width.label',
		defaultMessage: 'Select width: {selectedWidth}',
		description:
			'Accessible label for the header image width dropdown button, including the currently selected value',
	},
});

type FullWidthDropdownMenuProps = {
	userOffline?: boolean;
	onImageWidthChange: (coverPictureWidth: COVERPICTUREWIDTH) => void;
	selectedWidth: COVERPICTUREWIDTH;
	onCoverImageWidthDropdownOptionSelected?: (isCoverImageWidthChanged: boolean) => void;
};

export const FullWidthDropdownMenu = ({
	userOffline,
	onImageWidthChange,
	selectedWidth = COVERPICTUREWIDTH.FULL,
	onCoverImageWidthDropdownOptionSelected,
}: FullWidthDropdownMenuProps): React.JSX.Element => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const intl = useIntl();

	const createWidthToggleDropdownAnalytics = useCallback(
		(coverPictureWidth: COVERPICTUREWIDTH) => {
			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					action: 'clicked',
					source: `widthToggleDropdownButton`,
					actionSubject: 'button',
					actionSubjectId: `${coverPictureWidth}WidthToggleDropdownButton`,
				},
			}).fire();
		},
		[createAnalyticsEvent],
	);

	return (
		<DropdownMenu
			placement="bottom-start"
			zIndex={layers.dialog()}
			shouldRenderToParent
			shouldFitContainer
			trigger={({ triggerRef, ...props }) => {
				const selectedLabel =
					selectedWidth === COVERPICTUREWIDTH.FULL
						? intl.formatMessage(i18n.fullWidthSelectOption)
						: intl.formatMessage(i18n.fixedWidthSelectOption);

				return (
					<Button
						{...props}
						iconAfter={<ChevronDown label="" size="small" />}
						ref={triggerRef}
						shouldFitContainer
						// eslint-disable-next-line @atlaskit/design-system/no-unsafe-style-overrides, @atlaskit/ui-styling-standard/enforce-style-prop
						style={{ textAlign: 'left' }}
						isDisabled={userOffline}
						aria-label={intl.formatMessage(i18n.selectWidthLabel, {
							selectedWidth: selectedLabel,
						})}
					>
						{selectedWidth === COVERPICTUREWIDTH.FULL ? (
							<FormattedMessage {...i18n.fullWidthSelectOption} />
						) : (
							<FormattedMessage {...i18n.fixedWidthSelectOption} />
						)}
					</Button>
				);
			}}
		>
			<DropdownItemGroup>
				<DropdownItem
					key={COVERPICTUREWIDTH.FULL}
					onClick={() => {
						onImageWidthChange(COVERPICTUREWIDTH.FULL);
						createWidthToggleDropdownAnalytics(COVERPICTUREWIDTH.FULL);
						onCoverImageWidthDropdownOptionSelected?.(selectedWidth !== COVERPICTUREWIDTH.FULL);
					}}
					testId="coverPictureFullWidthToggleDropdownItem"
				>
					<FormattedMessage {...i18n.fullWidthSelectOption} />
				</DropdownItem>
				<DropdownItem
					key={COVERPICTUREWIDTH.FIXED}
					onClick={() => {
						onImageWidthChange(COVERPICTUREWIDTH.FIXED);
						createWidthToggleDropdownAnalytics(COVERPICTUREWIDTH.FIXED);
						onCoverImageWidthDropdownOptionSelected?.(selectedWidth !== COVERPICTUREWIDTH.FIXED);
					}}
					testId="coverPictureFixedWidthToggleDropdownItem"
				>
					<FormattedMessage {...i18n.fixedWidthSelectOption} />
				</DropdownItem>
			</DropdownItemGroup>
		</DropdownMenu>
	);
};
